/**
 * ReplyDraftsModal — generate 3 AI-drafted replies to an inbound email
 * reply, let operator pick + edit + send via SMTP/SendGrid/Mailgun engine.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Sparkles, Send, Loader2, Copy, CheckCircle2, X } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReplyDraftsModal({ open, onClose, reply, onSent }) {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [busy, setBusy] = useState(false);
  const [draftSet, setDraftSet] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !reply) return;
    setDraftSet(null);
    setSelectedIdx(0);
    setBody("");
    setSubject(`Re: ${reply.subject || (isPt ? "(sem assunto)" : "(no subject)")}`);
    setBusy(true);
    axios.post(`${API}/reply-drafts/generate`, {
      reply_id: reply.id,
      sender_name: "AgenteLead Pro",
      language: isPt ? "pt" : "en",
    }).then(({ data }) => {
      setDraftSet(data);
      if ((data.drafts || []).length > 0) setBody(data.drafts[0].body || "");
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || e.message);
      onClose();
    }).finally(() => setBusy(false));
  }, [open, reply, isPt, onClose]);

  // Sync body when user switches angle
  useEffect(() => {
    if (!draftSet) return;
    const d = (draftSet.drafts || [])[selectedIdx];
    if (d) setBody(d.body || "");
  }, [selectedIdx, draftSet]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(body); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch (_e) { /* ignore */ }
  };

  const send = async () => {
    if (!draftSet) return;
    const angle = (draftSet.drafts || [])[selectedIdx];
    if (!angle) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/reply-drafts/send`, {
        draft_set_id: draftSet.draft_set_id,
        angle_id: angle.angle_id,
        subject,
        body,
      });
      toast.success(data.simulated
        ? (isPt ? "Enviado em modo simulado (configure provedor em Email Config)" : "Sent in simulated mode (configure provider in Email Config)")
        : (isPt ? "Resposta enviada" : "Reply sent"));
      onSent && onSent(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSending(false); }
  };

  if (!reply) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="reply-drafts-modal"
        className="max-w-3xl max-h-[92vh] overflow-y-auto bg-white"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[18px] font-bold">
            <Sparkles className="w-5 h-5 text-[#8B5CF6]" />
            {isPt ? "Rascunhos de resposta por IA" : "AI reply drafts"}
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-violet-100 text-violet-700 uppercase">
              {reply.intent}
            </span>
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-[#6B7280]">
            <b>De:</b> {reply.from_email} · <b>{isPt ? "Assunto" : "Subject"}:</b> {reply.subject || "—"}
          </DialogDescription>
        </DialogHeader>

        {/* Inbound quote */}
        <div className="bg-[#F9FAFB] border-l-4 border-[#8B5CF6] rounded p-3 text-[12px] text-[#374151] whitespace-pre-wrap max-h-[140px] overflow-y-auto">
          {(reply.body_preview || "").slice(0, 1200)}
        </div>

        {busy ? (
          <div className="py-10 flex items-center justify-center text-[#6B7280] text-[13px]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            {isPt ? "Gerando 3 rascunhos (GPT)..." : "Generating 3 drafts (GPT)..."}
          </div>
        ) : draftSet ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5" data-testid="draft-angle-tabs">
              {(draftSet.drafts || []).map((d, i) => (
                <button
                  key={d.id}
                  type="button"
                  data-testid={`draft-angle-${d.angle_id}`}
                  onClick={() => setSelectedIdx(i)}
                  className={
                    "px-2 py-2 rounded-lg text-[11.5px] font-bold text-left transition-colors border " +
                    (selectedIdx === i
                      ? "bg-[#8B5CF6] text-white border-[#8B5CF6]"
                      : "bg-white text-[#374151] border-[#E5E7EB] hover:border-[#8B5CF6]")
                  }
                >
                  <span className="text-[9.5px] opacity-80 uppercase tracking-wider block">#{i + 1}</span>
                  {isPt ? d.angle_label_pt : d.angle_label_en}
                </button>
              ))}
            </div>

            <label className="block mt-3">
              <div className="text-[10.5px] uppercase text-[#6B7280] font-semibold mb-1.5">
                {isPt ? "Assunto" : "Subject"}
              </div>
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                data-testid="draft-subject"
                className="w-full al-input h-9 text-[13px] bg-white" />
            </label>

            <label className="block mt-3">
              <div className="text-[10.5px] uppercase text-[#6B7280] font-semibold mb-1.5 flex items-center justify-between">
                <span>{isPt ? "Corpo do email (editável)" : "Email body (editable)"}</span>
                <button
                  type="button"
                  onClick={copy}
                  data-testid="draft-copy"
                  className="text-[10.5px] font-semibold text-[#8B5CF6] hover:underline inline-flex items-center gap-1"
                >
                  {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied ? (isPt ? "Copiado" : "Copied") : (isPt ? "Copiar" : "Copy")}
                </button>
              </div>
              <textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)}
                data-testid="draft-body"
                className="w-full al-input p-3 text-[12.5px] leading-relaxed bg-white" />
            </label>
          </>
        ) : null}

        <div className="mt-4 flex items-center justify-end gap-2 pt-3 border-t border-[#E5E7EB]">
          <button type="button" onClick={onClose} data-testid="draft-cancel"
            className="px-4 h-9 rounded-lg border border-[#E5E7EB] text-[12.5px] font-semibold hover:bg-[#FAFAFB] inline-flex items-center gap-1.5">
            <X className="w-3.5 h-3.5" /> {isPt ? "Fechar" : "Close"}
          </button>
          <button type="button" onClick={send} disabled={sending || !draftSet || !body}
            data-testid="draft-send"
            className="px-5 h-9 rounded-lg bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {isPt ? "Enviar resposta" : "Send reply"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
